import React from 'react';
import { useTranslation } from 'react-i18next';
import { logError } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Flag components as SVG icons
const EnglishFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 12" fill="none">
    {/* Split US/GB flag */}
    {/* GB side (left) */}
    <rect x="0" y="0" width="12" height="12" fill="#012169" />
    <path d="M0 0 L12 12 M12 0 L0 12" stroke="#ffffff" strokeWidth="1.2" />
    <path d="M0 0 L12 12 M12 0 L0 12" stroke="#C8102E" strokeWidth="0.6" />
    <rect x="5.2" y="0" width="1.6" height="12" fill="#ffffff" />
    <rect x="0" y="5.2" width="12" height="1.6" fill="#ffffff" />
    <rect x="5.6" y="0" width="0.8" height="12" fill="#C8102E" />
    <rect x="0" y="5.6" width="12" height="0.8" fill="#C8102E" />

    {/* US side (right) */}
    <rect x="12" y="0" width="12" height="12" fill="#B22234" />
    <rect x="12" y="0" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="1.8" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="3.6" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="5.4" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="7.2" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="9" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="10.8" width="12" height="0.9" fill="#ffffff" />
    <rect x="12" y="0" width="5" height="6" fill="#3C3B6E" />
    <circle cx="14" cy="1" r="0.3" fill="#ffffff" />
    <circle cx="15.5" cy="1" r="0.3" fill="#ffffff" />
    <circle cx="17" cy="1" r="0.3" fill="#ffffff" />
    <circle cx="14.75" cy="2" r="0.3" fill="#ffffff" />
    <circle cx="16.25" cy="2" r="0.3" fill="#ffffff" />
    <circle cx="14" cy="3" r="0.3" fill="#ffffff" />
    <circle cx="15.5" cy="3" r="0.3" fill="#ffffff" />
    <circle cx="17" cy="3" r="0.3" fill="#ffffff" />
    <circle cx="14.75" cy="4" r="0.3" fill="#ffffff" />
    <circle cx="16.25" cy="4" r="0.3" fill="#ffffff" />
    <circle cx="14" cy="5" r="0.3" fill="#ffffff" />
    <circle cx="15.5" cy="5" r="0.3" fill="#ffffff" />
    <circle cx="17" cy="5" r="0.3" fill="#ffffff" />
  </svg>
);

const SpanishFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 12" fill="none">
    <rect x="0" y="0" width="24" height="3" fill="#C60B1E" />
    <rect x="0" y="3" width="24" height="6" fill="#FFC400" />
    <rect x="0" y="9" width="24" height="3" fill="#C60B1E" />
    {/* Simplified coat of arms */}
    <rect x="6" y="4.5" width="3" height="3" fill="#AA151B" rx="0.5" />
    <rect x="6.5" y="5" width="2" height="2" fill="#F1BF00" />
  </svg>
);

const GermanFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 12" fill="none">
    <rect x="0" y="0" width="24" height="4" fill="#000000" />
    <rect x="0" y="4" width="24" height="4" fill="#DE0000" />
    <rect x="0" y="8" width="24" height="4" fill="#FFCE00" />
  </svg>
);

const BrazilFlag = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 12" fill="none">
    <rect x="0" y="0" width="24" height="12" fill="#009739" />
    <path d="M3 6 L12 1.5 L21 6 L12 10.5 Z" fill="#FEDD00" />
    <circle cx="12" cy="6" r="2.2" fill="#012169" />
    <path d="M10.5 6 Q12 5 13.5 6" stroke="#FEDD00" strokeWidth="0.3" fill="none" />
  </svg>
);

const languages = [
  {
    code: 'en',
    name: 'English',
    flag: EnglishFlag,
  },
  {
    code: 'es',
    name: 'Español',
    flag: SpanishFlag,
  },
  {
    code: 'de',
    name: 'Deutsch',
    flag: GermanFlag,
  },
  {
    code: 'pt',
    name: 'Português',
    flag: BrazilFlag,
  },
];

export function LanguageToggle() {
  const { i18n } = useTranslation();

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    try {
      i18n.changeLanguage(languageCode);
      // Persist language choice in localStorage
      localStorage.setItem('kernlogic-language', languageCode);
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'LanguageToggle',
        action: 'handleLanguageChange',
        metadata: {
          languageCode,
          context: 'language_change_failure',
        },
      });
    }
  };

  const CurrentFlag = currentLanguage.flag;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Current language: ${currentLanguage.name}. Click to change language`}
        >
          <CurrentFlag className="size-5 rounded-sm border border-border/20" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map(language => {
          const FlagComponent = language.flag;
          const isActive = language.code === i18n.language;
          return (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`flex cursor-pointer items-center gap-3 ${
                isActive ? 'bg-accent text-accent-foreground' : ''
              }`}
            >
              <FlagComponent className="size-4 rounded-sm border border-border/20" />
              <span className={`${isActive ? 'font-semibold' : ''}`}>{language.name}</span>
              {isActive && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
